/**
 * Yearly Summary Component
 * 
 * Displays a yearly travel recap with shareable summary cards
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Alert,
  Image,
  Pressable,
} from 'react-native';
import * as Sharing from 'expo-sharing';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { getYearlyUserStats } from '@/services/userStats';
import { fetchUserTrips } from '@/services/trips';
import { getTripsEligibleForTravelStats } from '@/services/gamification';
import { captureRecapShareImage } from '@/services/recapShareImage';
import { maybePromptForRating } from '@/services/ratingPrompt';
import type { Trip } from '@/services/trips';

type YearlySummaryProps = {
  year?: number;
};

export function YearlySummary({ year }: YearlySummaryProps) {
  const theme = useTheme();
  const router = useRouter();
  const handleClose = () => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/profile');
  };

  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [yearlyStats, setYearlyStats] = useState<{
    countries: number;
    cities: number;
    tripDays: number;
    trips: number;
  } | null>(null);
  const [yearTrips, setYearTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [isCapturingShare, setIsCapturingShare] = useState(false);
  const shareCaptureRef = useRef<View | null>(null);

  const currentYear = year || new Date().getFullYear();

  const closeHeaderAction = (
    <Pressable
      onPress={handleClose}
      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
      accessibilityLabel="Close"
      accessibilityRole="button"
    >
      <Ionicons name="close" size={24} color={theme.colors.text} />
    </Pressable>
  );

  useEffect(() => {
    loadYearlySummary();
  }, [firebaseUser?.uid, currentYear]);

  const loadYearlySummary = async () => {
    if (!firebaseUser?.uid) return;

    try {
      setSummaryError(null);
      setLoading(true);
      const [yearStats, tripsResult] = await Promise.all([
        getYearlyUserStats(firebaseUser.uid, currentYear),
        fetchUserTrips(firebaseUser.uid),
      ]);

      setYearlyStats(yearStats);

      const completedTrips = getTripsEligibleForTravelStats(tripsResult.trips);
      const filteredTrips = completedTrips.filter(
        (trip) =>
          trip.startDate.getFullYear() === currentYear ||
          trip.endDate.getFullYear() === currentYear
      );
      setYearTrips(filteredTrips);
    } catch (error) {
      console.error('[YearlySummary] Error loading summary:', error);
      setSummaryError("Couldn't load your yearly summary. Check your connection and try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadYearlySummary();
  };

  const handleShare = async () => {
    if (!yearlyStats) return;

    try {
      setIsCapturingShare(true);
      await new Promise((resolve) => setTimeout(resolve, 120));
      const captureUri = await captureRecapShareImage(shareCaptureRef);
      const canShareImage = await Sharing.isAvailableAsync();
      if (!canShareImage) {
        throw new Error('Image sharing is unavailable on this device.');
      }
      await Sharing.shareAsync(captureUri, {
        dialogTitle: `My ${currentYear} Travel Summary`,
        mimeType: 'image/jpeg',
        UTI: 'public.jpeg',
      });
      void maybePromptForRating({
        trigger: 'yearly_summary_share_success',
        source: 'yearly_summary_share',
        deferMs: 350,
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('User did not share')) {
        return;
      }
      console.error('[YearlySummary] Error sharing:', error);
      Alert.alert(
        'Unable to share summary image',
        error instanceof Error ? error.message : 'Please try again.'
      );
    } finally {
      setIsCapturingShare(false);
    }
  };

  // Find favorite destination (most visited) and best image for it.
  const getFavoriteDestination = (): { label: string; imageUrl: string | null } | null => {
    if (!yearTrips.length) return null;

    const destinationCounts: Record<string, { label: string; count: number; imageUrl: string | null }> = {};
    for (const trip of yearTrips) {
      if (trip.destinations && trip.destinations.length > 0) {
        for (const dest of trip.destinations) {
          const city = dest.city?.trim();
          const country = dest.country?.trim();
          if (!city) continue;
          const label = country ? `${city}, ${country}` : city;
          const key = `${city.toLowerCase()}|${(country || '').toLowerCase()}`;
          const existing = destinationCounts[key];
          destinationCounts[key] = {
            label,
            count: (existing?.count || 0) + 1,
            imageUrl: existing?.imageUrl || trip.landmarkImage || null,
          };
        }
      } else if (trip.destination) {
        const label = trip.destination.trim();
        if (!label) continue;
        const key = label.toLowerCase();
        const existing = destinationCounts[key];
        destinationCounts[key] = {
          label,
          count: (existing?.count || 0) + 1,
          imageUrl: existing?.imageUrl || trip.landmarkImage || null,
        };
      }
    }

    const favorite = Object.values(destinationCounts).sort((a, b) => b.count - a.count)[0];
    return favorite ? { label: favorite.label, imageUrl: favorite.imageUrl } : null;
  };

  if (loading && !yearlyStats) {
    return (
      <Screen
        title={`Your ${currentYear} in Travel`}
        headerAction={closeHeaderAction}
        scrollable={false}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      </Screen>
    );
  }

  if (summaryError && !yearlyStats) {
    return (
      <Screen
        title={`Your ${currentYear} in Travel`}
        headerAction={closeHeaderAction}
        scrollable={false}
      >
        <ErrorState
          variant="full"
          title="Couldn't load yearly summary"
          message={summaryError}
          onRetry={loadYearlySummary}
          retryLabel="Retry"
        />
      </Screen>
    );
  }

  if (!yearlyStats || yearlyStats.trips === 0) {
    return (
      <Screen
        title={`Your ${currentYear} in Travel`}
        subtitle="No trips recorded for this year"
        headerAction={closeHeaderAction}
        scrollable={false}
      >
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.content}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {summaryError ? (
            <ErrorState
              title="Couldn't refresh yearly summary"
              message={summaryError}
              onRetry={loadYearlySummary}
              retryLabel="Retry"
            />
          ) : null}
        </ScrollView>
      </Screen>
    );
  }

  const favoriteDestination = getFavoriteDestination();
  const summaryCardBackground = theme.colorScheme === 'dark' ? theme.colors.surface : '#F8F9FA';
  const subtleSurfaceBackground = theme.colorScheme === 'dark' ? theme.colors.surfaceElevated : '#F8F9FA';
  const imageFallbackBackground = theme.colorScheme === 'dark' ? theme.colors.backgroundSecondary : '#EEF1F4';

  return (
    <Screen
      title={`Your ${currentYear} in Travel`}
      subtitle="A year of adventures and discoveries"
      headerAction={closeHeaderAction}
      scrollable={false}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View
          ref={shareCaptureRef}
          collapsable={false}
          style={[styles.shareCaptureContainer, { backgroundColor: theme.colors.background }]}
        >
          {summaryError ? (
            <View style={{ marginBottom: 16 }}>
              <ErrorState
                title="Couldn't refresh yearly summary"
                message={summaryError}
                onRetry={loadYearlySummary}
                retryLabel="Retry"
              />
            </View>
          ) : null}

          {/* Summary Card */}
          <Card
            variant="elevated"
            elevation={2}
            padding={24}
            style={[styles.summaryCard, { backgroundColor: summaryCardBackground }]}
          >
            <View style={styles.summaryGrid}>
              <View style={styles.summaryItem}>
                <Heading style={[styles.summaryValue, { color: theme.colors.text }]}>{yearlyStats.trips}</Heading>
                <Body numberOfLines={1} style={[styles.summaryLabel, { color: theme.colors.textSecondary }]}>
                  {yearlyStats.trips === 1 ? 'Trip' : 'Trips'}
                </Body>
              </View>
              <View style={styles.summaryItem}>
                <Heading style={[styles.summaryValue, { color: theme.colors.text }]}>{yearlyStats.countries}</Heading>
                <Body numberOfLines={1} style={[styles.summaryLabel, { color: theme.colors.textSecondary }]}>
                  {yearlyStats.countries === 1 ? 'Country' : 'Countries'}
                </Body>
              </View>
              <View style={styles.summaryItem}>
                <Heading style={[styles.summaryValue, { color: theme.colors.text }]}>{yearlyStats.cities}</Heading>
                <Body numberOfLines={1} style={[styles.summaryLabel, { color: theme.colors.textSecondary }]}>
                  {yearlyStats.cities === 1 ? 'City' : 'Cities'}
                </Body>
              </View>
              <View style={styles.summaryItem}>
                <Heading style={[styles.summaryValue, { color: theme.colors.text }]}>{yearlyStats.tripDays}</Heading>
                <Body numberOfLines={1} style={[styles.summaryLabel, { color: theme.colors.textSecondary }]}>
                  {yearlyStats.tripDays === 1 ? 'Day' : 'Days'}
                </Body>
              </View>
            </View>
          </Card>

          {/* Favorite Destination */}
          {favoriteDestination && (
            <Card variant="elevated" elevation={1} padding={16} style={styles.favoriteCard}>
              <View style={styles.favoriteHeader}>
                <Heading style={styles.favoriteTitle}>Favorite Destination</Heading>
                <Body style={{ color: theme.colors.textSecondary }}>
                  Your most visited place this year
                </Body>
              </View>
              <View style={[styles.favoriteContent, { backgroundColor: subtleSurfaceBackground }]}>
                {favoriteDestination.imageUrl ? (
                  <Image
                    source={{ uri: favoriteDestination.imageUrl }}
                    style={[styles.favoriteImage, { backgroundColor: imageFallbackBackground }]}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={[styles.favoriteImageFallback, { backgroundColor: imageFallbackBackground }]}>
                    <Caption style={{ color: theme.colors.textSecondary }}>No image available</Caption>
                  </View>
                )}
                <View style={[styles.favoriteLabelContainer, { backgroundColor: subtleSurfaceBackground }]}>
                  <Heading style={[styles.favoriteDestination, { color: theme.colors.text }]}>
                    {favoriteDestination.label}
                  </Heading>
                </View>
              </View>
            </Card>
          )}

          {/* Trips list is hidden only while generating share snapshot for a cleaner card. */}
          {!isCapturingShare && yearTrips.length > 0 && (
            <Card variant="elevated" elevation={1} padding={16} style={styles.tripsCard}>
              <Heading style={styles.sectionTitle}>Your Trips</Heading>
              <View style={styles.tripsList}>
                {yearTrips.map((trip) => (
                  <View key={trip.id} style={[styles.tripItem, { borderBottomColor: theme.colors.borderMuted }]}>
                    <Body style={[styles.tripName, { color: theme.colors.text }]}>{trip.name}</Body>
                    <Caption style={{ color: theme.colors.textTertiary }}>
                      {trip.startDate.toLocaleDateString()} - {trip.endDate.toLocaleDateString()}
                    </Caption>
                  </View>
                ))}
              </View>
            </Card>
          )}
        </View>

        {/* Share Button */}
        <Button
          label="Share Summary"
          variant="secondary"
          onPress={handleShare}
          style={styles.shareButton}
        />
      </ScrollView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  summaryCard: {
    marginBottom: 16,
  },
  summaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  summaryItem: {
    flex: 1,
    minWidth: 64,
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: 36,
    lineHeight: 42,
    fontWeight: '700',
    marginBottom: 4,
  },
  summaryLabel: {
    textAlign: 'center',
    width: '100%',
  },
  favoriteCard: {
    marginBottom: 16,
  },
  favoriteHeader: {
    marginBottom: 12,
  },
  favoriteTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  favoriteContent: {
    overflow: 'hidden',
    borderRadius: 12,
  },
  favoriteImage: {
    width: '100%',
    height: 180,
  },
  favoriteImageFallback: {
    width: '100%',
    height: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  favoriteLabelContainer: {
    padding: 16,
  },
  favoriteDestination: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
  },
  tripsCard: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  tripsList: {
    gap: 12,
  },
  tripItem: {
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  tripName: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  shareButton: {
    marginTop: 8,
  },
  shareCaptureContainer: {
    borderRadius: 0,
    overflow: 'visible',
  },
});

